import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  Alert,
  Button,
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  Form,
  FormFeedback,
  Input,
  Label,
  Nav,
  NavItem,
  NavLink,
  Row,
  Spinner,
  TabContent,
  TabPane,
} from "reactstrap";
import classnames from "classnames";
import * as Yup from "yup";
import { useFormik } from "formik";
import { useTranslation } from "react-i18next";
import { toast, ToastContainer } from "react-toastify";
import BreadCrumb from "../../../Components/Common/BreadCrumb";
import CampaignEasyEmailEditor from "../../Content/Campaigns/CampaignEasyEmailEditor";
import {
  createSuperAdminPlatformTemplate,
  getSuperAdminPlatformTemplate,
  importSuperAdminPlatformTemplateHtml,
  PLATFORM_TEMPLATE_CATEGORIES,
  PLATFORM_TEMPLATE_GALLERY_TAGS,
  PlatformTemplateCategory,
  regenerateSuperAdminPlatformTemplateThumbnail,
  updateSuperAdminPlatformTemplate,
  uploadSuperAdminPlatformTemplateThumbnail,
  deleteSuperAdminPlatformTemplateThumbnail,
} from "../../../helpers/super_admin_platform_templates_helper";

type FormValues = {
  name: string;
  category: PlatformTemplateCategory;
  description_pt_BR: string;
  description_en: string;
  description_es: string;
  html_body: string;
  editor_design: Record<string, unknown> | null;
  is_active: boolean;
  sort_order: number;
  tags: string[];
  variant_group: string;
  variant_label: string;
  season_starts_on: string;
  season_ends_on: string;
};

type ContentTab = "editor" | "source" | "preview";
type PreviewMode = "desktop" | "mobile";

/**
 * Form SA — guia do catálogo com Easy Email (DOC 02 § 8 · LAC-024).
 */
const PlatformTemplateForm = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEdit = Boolean(id);

  document.title = isEdit
    ? t("sa.platformTemplates.form.documentTitleEdit")
    : t("sa.platformTemplates.form.documentTitleNew");

  const [loading, setLoading] = useState(isEdit);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<ContentTab>("editor");
  const [previewMode, setPreviewMode] = useState<PreviewMode>("desktop");
  const [initialValues, setInitialValues] = useState<FormValues>({
    name: "",
    category: "other",
    description_pt_BR: "",
    description_en: "",
    description_es: "",
    html_body: "",
    editor_design: null,
    is_active: true,
    sort_order: 0,
    tags: [],
    variant_group: "",
    variant_label: "",
    season_starts_on: "",
    season_ends_on: "",
  });
  const [editorRemountKey, setEditorRemountKey] = useState("boot");
  const [importUrl, setImportUrl] = useState("");
  const [importBusy, setImportBusy] = useState(false);
  const [thumbnailUrl, setThumbnailUrl] = useState<string | null>(null);
  const [thumbBusy, setThumbBusy] = useState(false);

  const validationSchema = useMemo(
    () =>
      Yup.object({
        name: Yup.string()
          .trim()
          .max(255, t("sa.platformTemplates.form.validation.nameMax"))
          .required(t("sa.platformTemplates.form.validation.nameRequired")),
        html_body: Yup.string().required(
          t("sa.platformTemplates.form.validation.htmlRequired")
        ),
        sort_order: Yup.number()
          .min(0)
          .max(999999)
          .required(),
      }),
    [t, i18n.language]
  );

  const buildDescription = (values: FormValues) => {
    const description: Record<string, string> = {};
    if (values.description_pt_BR.trim()) {
      description.pt_BR = values.description_pt_BR.trim();
    }
    if (values.description_en.trim()) {
      description.en = values.description_en.trim();
    }
    if (values.description_es.trim()) {
      description.es = values.description_es.trim();
    }
    return Object.keys(description).length ? description : null;
  };

  const validation = useFormik<FormValues>({
    enableReinitialize: true,
    initialValues,
    validationSchema,
    onSubmit: async (values) => {
      setSaving(true);
      setFormError(null);
      const payload = {
        name: values.name.trim(),
        category: values.category,
        description: buildDescription(values),
        html_body: values.html_body,
        editor_design: values.editor_design,
        is_active: values.is_active,
        sort_order: Number(values.sort_order) || 0,
        tags: values.tags,
        variant_group: values.variant_group.trim() || null,
        variant_label: values.variant_label.trim() || null,
        season_starts_on: values.season_starts_on || null,
        season_ends_on: values.season_ends_on || null,
      };
      try {
        if (isEdit && id) {
          const res = await updateSuperAdminPlatformTemplate(
            Number(id),
            payload
          );
          toast.success(
            res.message || t("sa.platformTemplates.toast.updated"),
            { autoClose: 2500 }
          );
        } else {
          const res = await createSuperAdminPlatformTemplate(payload);
          toast.success(
            res.message || t("sa.platformTemplates.toast.created"),
            { autoClose: 2500 }
          );
        }
        navigate("/super-admin/platform-templates");
      } catch (err: any) {
        const details = err?.details;
        if (details && typeof details === "object") {
          const mapped: Record<string, string> = {};
          Object.keys(details).forEach((key) => {
            mapped[key] = Array.isArray(details[key])
              ? details[key][0]
              : String(details[key]);
          });
          validation.setErrors(mapped);
        }
        setFormError(err?.message || t("sa.platformTemplates.error.save"));
      } finally {
        setSaving(false);
      }
    },
  });

  useEffect(() => {
    if (!isEdit || !id) {
      setLoading(false);
      return;
    }

    let cancelled = false;
    (async () => {
      setLoading(true);
      setLoadError(null);
      try {
        const res = await getSuperAdminPlatformTemplate(Number(id));
        if (cancelled) {
          return;
        }
        if (!res?.success || !res.data) {
          setLoadError(t("sa.platformTemplates.error.loadOne"));
          return;
        }
        const desc = res.data.description || {};
        setInitialValues({
          name: res.data.name || "",
          category: (res.data.category as PlatformTemplateCategory) || "other",
          description_pt_BR: desc.pt_BR || "",
          description_en: desc.en || "",
          description_es: desc.es || "",
          html_body: res.data.html_body || "",
          editor_design: res.data.editor_design || null,
          is_active: Boolean(res.data.is_active),
          sort_order: res.data.sort_order ?? 0,
          tags: res.data.tags || [],
          variant_group: res.data.variant_group || "",
          variant_label: res.data.variant_label || "",
          season_starts_on: res.data.season_starts_on || "",
          season_ends_on: res.data.season_ends_on || "",
        });
        setThumbnailUrl(res.data.thumbnail_url || null);
        setEditorRemountKey(`server-${res.data.id}-${Date.now()}`);
      } catch (err: any) {
        if (!cancelled) {
          setLoadError(
            err?.message || t("sa.platformTemplates.error.loadOne")
          );
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [id, isEdit, t]);

  const onEditorChange = useCallback(
    (payload: {
      editor_design: Record<string, unknown>;
      html_body: string;
    }) => {
      validation.setFieldValue("editor_design", payload.editor_design);
      validation.setFieldValue("html_body", payload.html_body);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [validation.setFieldValue]
  );

  if (loading) {
    return (
      <div className="page-content">
        <Container fluid>
          <div className="text-center py-5">
            <Spinner color="primary" />
          </div>
        </Container>
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="page-content">
        <Container fluid>
          <Alert color="danger">{loadError}</Alert>
          <Button
            color="light"
            tag={Link}
            to="/super-admin/platform-templates"
          >
            {t("sa.platformTemplates.form.backToList")}
          </Button>
        </Container>
      </div>
    );
  }

  return (
    <div className="page-content">
      <Container fluid>
        <BreadCrumb
          title={
            isEdit
              ? t("sa.platformTemplates.form.titleEdit")
              : t("sa.platformTemplates.form.titleNew")
          }
          pageTitle={t("sa.platformTemplates.breadcrumb")}
        />
        <ToastContainer closeButton={false} limit={1} />

        {formError && (
          <Alert color="danger" toggle={() => setFormError(null)}>
            {formError}
          </Alert>
        )}

        <Form
          onSubmit={(e) => {
            e.preventDefault();
            validation.handleSubmit();
          }}
        >
          <Row>
            <Col lg={8}>
              <Card>
                <CardHeader>
                  <Nav tabs className="nav-tabs-custom card-header-tabs border-bottom-0">
                    <NavItem>
                      <NavLink
                        className={classnames({ active: activeTab === "editor" })}
                        href="#"
                        onClick={(e) => {
                          e.preventDefault();
                          setActiveTab("editor");
                        }}
                      >
                        {t("sa.platformTemplates.form.tab.editor")}
                      </NavLink>
                    </NavItem>
                    <NavItem>
                      <NavLink
                        className={classnames({ active: activeTab === "source" })}
                        href="#"
                        onClick={(e) => {
                          e.preventDefault();
                          setActiveTab("source");
                        }}
                      >
                        {t("sa.platformTemplates.form.tab.source")}
                      </NavLink>
                    </NavItem>
                    <NavItem>
                      <NavLink
                        className={classnames({ active: activeTab === "preview" })}
                        href="#"
                        onClick={(e) => {
                          e.preventDefault();
                          setActiveTab("preview");
                        }}
                      >
                        {t("sa.platformTemplates.form.tab.preview")}
                      </NavLink>
                    </NavItem>
                  </Nav>
                </CardHeader>
                <CardBody>
                  <TabContent activeTab={activeTab}>
                    <TabPane tabId="editor">
                      <CampaignEasyEmailEditor
                        design={validation.values.editor_design}
                        subject={validation.values.name || "Platform"}
                        remountKey={editorRemountKey}
                        onChange={onEditorChange}
                        galleryPath={null}
                      />
                      {validation.touched.html_body &&
                        validation.errors.html_body && (
                          <FormFeedback className="d-block">
                            {validation.errors.html_body}
                          </FormFeedback>
                        )}
                    </TabPane>
                    <TabPane tabId="source">
                      <Label htmlFor="html_body">
                        {t("sa.platformTemplates.form.htmlBody")}
                      </Label>
                      <Input
                        type="textarea"
                        id="html_body"
                        name="html_body"
                        rows={16}
                        value={validation.values.html_body}
                        onChange={(e) => {
                          validation.setFieldValue("html_body", e.target.value);
                          validation.setFieldValue("editor_design", null);
                          setEditorRemountKey(`source-${Date.now()}`);
                        }}
                        onBlur={validation.handleBlur}
                        invalid={
                          validation.touched.html_body &&
                          Boolean(validation.errors.html_body)
                        }
                      />
                      <FormFeedback>
                        {validation.errors.html_body}
                      </FormFeedback>
                      <p className="text-muted small mt-2 mb-0">
                        {t("sa.platformTemplates.form.htmlHelp")}
                      </p>
                    </TabPane>
                    <TabPane tabId="preview">
                      <div className="d-flex flex-wrap gap-2 mb-3">
                        <Button
                          size="sm"
                          color={previewMode === "desktop" ? "primary" : "light"}
                          onClick={() => setPreviewMode("desktop")}
                        >
                          {t("sa.platformTemplates.form.preview.desktop")}
                        </Button>
                        <Button
                          size="sm"
                          color={previewMode === "mobile" ? "primary" : "light"}
                          onClick={() => setPreviewMode("mobile")}
                        >
                          {t("sa.platformTemplates.form.preview.mobile")}
                        </Button>
                      </div>
                      {validation.values.html_body.trim() ? (
                        <div
                          className="mx-auto"
                          style={{
                            width: previewMode === "mobile" ? 375 : "100%",
                            maxWidth: "100%",
                          }}
                        >
                          <iframe
                            title={t("sa.platformTemplates.form.tab.preview")}
                            srcDoc={validation.values.html_body}
                            style={{
                              width: "100%",
                              minHeight: 480,
                              border: "1px solid var(--vz-border-color)",
                              borderRadius: 4,
                              background: "#fff",
                            }}
                          />
                        </div>
                      ) : (
                        <p className="text-muted mb-0">
                          {t("sa.platformTemplates.form.preview.emptyHtml")}
                        </p>
                      )}
                    </TabPane>
                  </TabContent>
                </CardBody>
              </Card>
            </Col>

            <Col lg={4}>
              <Card>
                <CardHeader>
                  <h5 className="card-title mb-0">
                    {t("sa.platformTemplates.form.details")}
                  </h5>
                </CardHeader>
                <CardBody>
                  <div className="mb-3">
                    <Label htmlFor="name">
                      {t("sa.platformTemplates.form.name")}
                    </Label>
                    <Input
                      id="name"
                      name="name"
                      value={validation.values.name}
                      onChange={validation.handleChange}
                      onBlur={validation.handleBlur}
                      invalid={
                        validation.touched.name &&
                        Boolean(validation.errors.name)
                      }
                      placeholder={t(
                        "sa.platformTemplates.form.namePlaceholder"
                      )}
                    />
                    <FormFeedback>{validation.errors.name}</FormFeedback>
                  </div>

                  <div className="mb-3">
                    <Label htmlFor="category">
                      {t("sa.platformTemplates.form.category")}
                    </Label>
                    <Input
                      type="select"
                      id="category"
                      name="category"
                      value={validation.values.category}
                      onChange={validation.handleChange}
                    >
                      {PLATFORM_TEMPLATE_CATEGORIES.map((cat) => (
                        <option key={cat} value={cat}>
                          {t(`platformTemplates.category.${cat}`)}
                        </option>
                      ))}
                    </Input>
                  </div>

                  <div className="mb-3">
                    <Label>{t("sa.platformTemplates.form.tags")}</Label>
                    <div className="d-flex flex-wrap gap-2">
                      {PLATFORM_TEMPLATE_GALLERY_TAGS.map((tag) => {
                        const checked = validation.values.tags.includes(tag);
                        return (
                          <Button
                            key={tag}
                            size="sm"
                            type="button"
                            color={checked ? "primary" : "light"}
                            onClick={() => {
                              const next = checked
                                ? validation.values.tags.filter((x) => x !== tag)
                                : [...validation.values.tags, tag];
                              validation.setFieldValue("tags", next);
                            }}
                          >
                            {t(`platformTemplates.tag.${tag}`, {
                              defaultValue: tag,
                            })}
                          </Button>
                        );
                      })}
                    </div>
                  </div>

                  <div className="mb-3">
                    <Label htmlFor="season_starts_on">
                      {t("sa.platformTemplates.form.season")}
                    </Label>
                    <div className="d-flex gap-2">
                      <Input
                        type="date"
                        id="season_starts_on"
                        name="season_starts_on"
                        value={validation.values.season_starts_on}
                        onChange={validation.handleChange}
                      />
                      <Input
                        type="date"
                        id="season_ends_on"
                        name="season_ends_on"
                        value={validation.values.season_ends_on}
                        onChange={validation.handleChange}
                      />
                    </div>
                  </div>

                  <div className="mb-3">
                    <Label htmlFor="variant_group">
                      {t("sa.platformTemplates.form.variant")}
                    </Label>
                    <div className="d-flex gap-2">
                      <Input
                        id="variant_group"
                        name="variant_group"
                        placeholder="promo-especial"
                        value={validation.values.variant_group}
                        onChange={validation.handleChange}
                      />
                      <Input
                        id="variant_label"
                        name="variant_label"
                        placeholder="A"
                        value={validation.values.variant_label}
                        onChange={validation.handleChange}
                        style={{ maxWidth: 72 }}
                      />
                    </div>
                  </div>

                  <div className="mb-3">
                    <Label htmlFor="importUrl">
                      {t("sa.platformTemplates.form.importUrl")}
                    </Label>
                    <div className="d-flex gap-2">
                      <Input
                        id="importUrl"
                        value={importUrl}
                        onChange={(e) => setImportUrl(e.target.value)}
                        placeholder="https://"
                      />
                      <Button
                        type="button"
                        color="soft-primary"
                        disabled={importBusy || !importUrl.trim()}
                        onClick={async () => {
                          setImportBusy(true);
                          try {
                            const res =
                              await importSuperAdminPlatformTemplateHtml(
                                importUrl.trim()
                              );
                            validation.setFieldValue(
                              "html_body",
                              res.data.html_body
                            );
                            validation.setFieldValue("editor_design", null);
                            setActiveTab("source");
                            toast.success(
                              res.message ||
                                t("sa.platformTemplates.toast.imported")
                            );
                          } catch (err: any) {
                            toast.error(
                              err?.message ||
                                t("sa.platformTemplates.error.import")
                            );
                          } finally {
                            setImportBusy(false);
                          }
                        }}
                      >
                        {importBusy ? <Spinner size="sm" /> : t("sa.platformTemplates.form.importAction")}
                      </Button>
                    </div>
                  </div>

                  {isEdit && id ? (
                    <div className="mb-3">
                      <Label>{t("sa.platformTemplates.form.thumbnail")}</Label>
                      {thumbnailUrl ? (
                        <img
                          src={thumbnailUrl}
                          alt=""
                          className="img-fluid rounded border mb-2"
                          style={{ maxHeight: 120 }}
                        />
                      ) : null}
                      <Input
                        type="file"
                        accept="image/png,image/jpeg,image/webp"
                        disabled={thumbBusy}
                        onChange={async (e) => {
                          const file = e.target.files?.[0];
                          if (!file) return;
                          setThumbBusy(true);
                          try {
                            const res =
                              await uploadSuperAdminPlatformTemplateThumbnail(
                                Number(id),
                                file
                              );
                            setThumbnailUrl(res.data.thumbnail_url);
                            toast.success(
                              res.message ||
                                t("sa.platformTemplates.toast.thumbnail")
                            );
                          } catch (err: any) {
                            toast.error(
                              err?.message ||
                                t("sa.platformTemplates.error.thumbnail")
                            );
                          } finally {
                            setThumbBusy(false);
                            e.target.value = "";
                          }
                        }}
                      />
                      {thumbnailUrl ? (
                        <Button
                          type="button"
                          color="link"
                          size="sm"
                          className="px-0 me-3"
                          disabled={thumbBusy}
                          onClick={async () => {
                            setThumbBusy(true);
                            try {
                              await deleteSuperAdminPlatformTemplateThumbnail(
                                Number(id)
                              );
                              setThumbnailUrl(null);
                            } finally {
                              setThumbBusy(false);
                            }
                          }}
                        >
                          {t("sa.platformTemplates.form.thumbnailClear")}
                        </Button>
                      ) : null}
                      <Button
                        type="button"
                        color="soft-secondary"
                        size="sm"
                        disabled={thumbBusy}
                        onClick={async () => {
                          setThumbBusy(true);
                          try {
                            const res =
                              await regenerateSuperAdminPlatformTemplateThumbnail(
                                Number(id),
                                true
                              );
                            setThumbnailUrl(res.data.thumbnail_url);
                            toast.success(
                              res.message ||
                                t("sa.platformTemplates.toast.thumbnailRegen")
                            );
                          } catch (err: any) {
                            toast.error(
                              err?.message ||
                                t("sa.platformTemplates.error.thumbnail")
                            );
                          } finally {
                            setThumbBusy(false);
                          }
                        }}
                      >
                        {thumbBusy ? (
                          <Spinner size="sm" />
                        ) : (
                          t("sa.platformTemplates.form.thumbnailRegen")
                        )}
                      </Button>
                    </div>
                  ) : null}

                  <div className="mb-3">
                    <Label htmlFor="description_pt_BR">
                      {t("sa.platformTemplates.form.descriptionPt")}
                    </Label>
                    <Input
                      type="textarea"
                      id="description_pt_BR"
                      name="description_pt_BR"
                      rows={2}
                      value={validation.values.description_pt_BR}
                      onChange={validation.handleChange}
                    />
                  </div>
                  <div className="mb-3">
                    <Label htmlFor="description_en">
                      {t("sa.platformTemplates.form.descriptionEn")}
                    </Label>
                    <Input
                      type="textarea"
                      id="description_en"
                      name="description_en"
                      rows={2}
                      value={validation.values.description_en}
                      onChange={validation.handleChange}
                    />
                  </div>
                  <div className="mb-3">
                    <Label htmlFor="description_es">
                      {t("sa.platformTemplates.form.descriptionEs")}
                    </Label>
                    <Input
                      type="textarea"
                      id="description_es"
                      name="description_es"
                      rows={2}
                      value={validation.values.description_es}
                      onChange={validation.handleChange}
                    />
                    <p className="text-muted small mt-1 mb-0">
                      {t("sa.platformTemplates.form.descriptionHelp")}
                    </p>
                  </div>

                  <div className="mb-3">
                    <Label htmlFor="sort_order">
                      {t("sa.platformTemplates.form.sortOrder")}
                    </Label>
                    <Input
                      type="number"
                      id="sort_order"
                      name="sort_order"
                      min={0}
                      value={validation.values.sort_order}
                      onChange={validation.handleChange}
                    />
                  </div>

                  <div className="form-check form-switch mb-4">
                    <Input
                      type="checkbox"
                      className="form-check-input"
                      id="is_active"
                      checked={validation.values.is_active}
                      onChange={(e) =>
                        validation.setFieldValue("is_active", e.target.checked)
                      }
                    />
                    <Label className="form-check-label" htmlFor="is_active">
                      {t("sa.platformTemplates.form.isActive")}
                    </Label>
                  </div>

                  <div className="d-flex flex-wrap gap-2">
                    <Button color="primary" type="submit" disabled={saving}>
                      {saving ? (
                        <Spinner size="sm" />
                      ) : (
                        t("sa.platformTemplates.form.save")
                      )}
                    </Button>
                    <Button
                      color="light"
                      tag={Link}
                      to="/super-admin/platform-templates"
                      disabled={saving}
                    >
                      {t("sa.platformTemplates.form.cancel")}
                    </Button>
                  </div>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Form>
      </Container>
    </div>
  );
};

export default PlatformTemplateForm;
